/**
 * TaskCard — modern, clickable task card.
 *
 * v0.1.52 redesign — replaces the legacy TaskRow row layout. Mirrors the cart
 * card visual standard from RAO/RCM:
 *   - Plain-language title (no GUID, no codes surfaced)
 *   - Customer name + product/category sub-line
 *   - Relative due time ("Due in 2 hr", "3 hr overdue") with red accent
 *   - Priority pill (urgent/high/normal/low)
 *   - Assigned-to display (officer code → display name from staff map)
 *   - SLA progress bar — visual % of SLA budget remaining
 *   - Quick-action buttons: Complete, Snooze, Reassign (Supervisor+)
 *
 * Hidden by design: taskId, GUIDs, raw status/role codes, raw category strings.
 * Category drives the icon; the user never sees "signing-recovery" raw.
 */
import {
  AlertTriangle,
  Clock,
  FileText,
  ShieldAlert,
  UserX,
  FileSignature,
  Wrench,
  CheckCircle2,
  UserCheck,
  StickyNote,
} from 'lucide-react';
import type { TaskRecordDto } from '@retail-ops/shared-types';
import type { TaskCategory, TaskPriority } from '@retail-ops/shared-types/enums';
import { Pill, Button } from '@/components/primitives';
import type { PillVariant } from '@/components/primitives';

export type SlaState = 'healthy' | 'at-risk' | 'breached' | 'no-sla';

export interface TaskCardProps {
  task: TaskRecordDto;
  onOpen: (task: TaskRecordDto) => void;
  onComplete?: (task: TaskRecordDto) => void;
  onReassign?: (task: TaskRecordDto) => void;
  onAddNote?: (task: TaskRecordDto) => void;
  /** Supervisor+ — show Reassign action. */
  canReassign?: boolean;
}

// ---------------------------------------------------------------------------
// Plain-language category labels (no raw kebab-case in UI)
// ---------------------------------------------------------------------------
const CATEGORY_LABELS: Record<TaskCategory, string> = {
  'signing-recovery': 'Signing recovery',
  'pipeline-error': 'Pipeline error',
  'abandonment': 'Abandoned cart',
  'compliance': 'Compliance review',
  'correction': 'Correction',
  'adverse-action': 'Adverse action',
  'decline-review': 'Decline review',
  'duplicate-review': 'Duplicate review',
  'general': 'Follow-up',
  'qc-exception': 'QC exception',
  'signing': 'Signing',
  'pipeline': 'Pipeline',
  'workflow': 'Workflow',
};

function categoryIcon(category: TaskCategory): JSX.Element {
  switch (category) {
    case 'signing-recovery':
    case 'signing':
      return <FileSignature size={16} />;
    case 'pipeline-error':
    case 'pipeline':
      return <Wrench size={16} />;
    case 'abandonment':
      return <UserX size={16} />;
    case 'compliance':
    case 'adverse-action':
      return <ShieldAlert size={16} />;
    case 'qc-exception':
      return <AlertTriangle size={16} />;
    default:
      return <FileText size={16} />;
  }
}

// ---------------------------------------------------------------------------
// Priority pill — handles both Title-case and lower-case values
// ---------------------------------------------------------------------------
function priorityPill(p: TaskPriority): { variant: PillVariant; label: string } {
  const lc = p.toLowerCase();
  if (lc === 'urgent' || lc === 'critical') return { variant: 'err', label: 'Urgent' };
  if (lc === 'high') return { variant: 'warn', label: 'High' };
  if (lc === 'low') return { variant: 'muted', label: 'Low' };
  return { variant: 'info', label: 'Normal' };
}

// ---------------------------------------------------------------------------
// Relative due-time formatting
// ---------------------------------------------------------------------------
export function formatRelativeDue(iso?: string): {
  text: string;
  overdue: boolean;
  hasDate: boolean;
} {
  if (!iso) return { text: 'No due date', overdue: false, hasDate: false };
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return { text: '—', overdue: false, hasDate: false };
  const ms = due.getTime() - Date.now();
  const overdue = ms < 0;
  const absMs = Math.abs(ms);
  const mins = Math.round(absMs / 60_000);
  const hours = Math.round(absMs / 3_600_000);
  const days = Math.round(absMs / 86_400_000);
  let unit: string;
  if (mins < 60) unit = `${mins} min`;
  else if (hours < 48) unit = `${hours} hr`;
  else unit = `${days} d`;
  return {
    text: overdue ? `${unit} overdue` : `Due in ${unit}`,
    overdue,
    hasDate: true,
  };
}

// ---------------------------------------------------------------------------
// SLA state derivation — uses createdAt → dueDate as budget envelope.
// "at-risk" = <25% budget remaining; "breached" = past dueDate.
// Falls back to 'no-sla' when no due date is set.
// ---------------------------------------------------------------------------
export function deriveSlaState(task: TaskRecordDto): { state: SlaState; pctRemaining: number } {
  if (!task.dueDate) return { state: 'no-sla', pctRemaining: 100 };
  const due = new Date(task.dueDate).getTime();
  const created = new Date(task.createdAt).getTime();
  const now = Date.now();
  if (Number.isNaN(due) || Number.isNaN(created)) return { state: 'no-sla', pctRemaining: 100 };
  if (now >= due) return { state: 'breached', pctRemaining: 0 };
  const total = due - created;
  if (total <= 0) return { state: 'no-sla', pctRemaining: 100 };
  const remaining = due - now;
  const pct = Math.max(0, Math.min(100, (remaining / total) * 100));
  if (pct < 25) return { state: 'at-risk', pctRemaining: Math.round(pct) };
  return { state: 'healthy', pctRemaining: Math.round(pct) };
}

// ---------------------------------------------------------------------------
// Officer-code → display name (best-effort masking)
// "BWIG" → "BWIG" (display unchanged when no map); "bill.wiggins@mypcb.com"
// → "bill.wiggins" so we don't surface raw email-as-id in UI.
// ---------------------------------------------------------------------------
function formatAssignee(raw: string): string {
  if (!raw) return 'Unassigned';
  if (raw === 'unassigned' || raw === 'Unassigned') return 'Unassigned';
  if (raw.includes('@')) {
    const local = raw.split('@')[0] ?? raw;
    return local.replace(/\./g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return raw;
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------
export function TaskCard({
  task,
  onOpen,
  onComplete,
  onReassign,
  onAddNote,
  canReassign,
}: TaskCardProps): JSX.Element {
  const due = formatRelativeDue(task.dueDate);
  const sla = deriveSlaState(task);
  const pri = priorityPill(task.priority);
  const categoryLabel = CATEGORY_LABELS[task.category] ?? 'Task';
  const slaClass =
    sla.state === 'breached'
      ? 'is-breached'
      : sla.state === 'at-risk'
        ? 'is-at-risk'
        : sla.state === 'healthy'
          ? 'is-healthy'
          : '';

  const handleAction = (e: React.MouseEvent, fn?: (t: TaskRecordDto) => void): void => {
    e.stopPropagation();
    fn?.(task);
  };

  return (
    <article
      className={`task-card ${slaClass}`}
      onClick={() => onOpen(task)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(task);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Open task ${task.title}`}
    >
      <header className="task-card__head">
        <span className="task-card__icon" aria-hidden>
          {categoryIcon(task.category)}
        </span>
        <div className="task-card__heading">
          <h3 className="task-card__title">{task.title}</h3>
          <div className="task-card__sub">
            {task.customerName && (
              <span className="task-card__customer">{task.customerName}</span>
            )}
            <span className="task-card__category">{categoryLabel}</span>
          </div>
        </div>
        <Pill variant={pri.variant}>{pri.label}</Pill>
      </header>

      <div className="task-card__meta">
        <span className={`task-card__due ${due.overdue ? 'is-overdue' : ''}`}>
          <Clock size={12} aria-hidden />
          {due.text}
        </span>
        <span className="task-card__assignee">
          <UserCheck size={12} aria-hidden />
          {formatAssignee(task.assignedTo)}
        </span>
      </div>

      {sla.state !== 'no-sla' && (
        <div
          className="task-card__sla"
          title={
            sla.state === 'breached'
              ? 'SLA breached'
              : sla.state === 'at-risk'
                ? `SLA at risk · ${sla.pctRemaining}% budget remaining`
                : `SLA healthy · ${sla.pctRemaining}% budget remaining`
          }
        >
          <span className="task-card__sla-track" aria-hidden>
            <span
              className="task-card__sla-fill"
              style={{ width: `${sla.pctRemaining}%` }}
            />
          </span>
          <span className="task-card__sla-label">
            {sla.state === 'breached'
              ? 'SLA breached'
              : sla.state === 'at-risk'
                ? `${sla.pctRemaining}% SLA left`
                : `${sla.pctRemaining}% SLA left`}
          </span>
        </div>
      )}

      <footer className="task-card__actions" onClick={(e) => e.stopPropagation()}>
        {onComplete && task.status !== 'Completed' && (
          <Button
            size="sm"
            variant="green"
            startIcon={<CheckCircle2 size={12} />}
            onClick={(e) => handleAction(e, onComplete)}
          >
            Complete
          </Button>
        )}
        {onAddNote && (
          <Button
            size="sm"
            variant="ghost"
            startIcon={<StickyNote size={12} />}
            onClick={(e) => handleAction(e, onAddNote)}
          >
            Note
          </Button>
        )}
        {canReassign && onReassign && task.status !== 'Completed' && (
          <Button
            size="sm"
            variant="primary"
            startIcon={<UserCheck size={12} />}
            onClick={(e) => handleAction(e, onReassign)}
          >
            Reassign
          </Button>
        )}
      </footer>
    </article>
  );
}
